import { useState, useCallback, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { SUBSCRIPTION_PLANS } from "../const/subscriptionPlan";

/**
 * Custom hook for subscription management
 * Provides subscription status, credits, expiration tracking, and management functions
 */
export const useSubscription = () => {
	const { user, refreshUser } = useAuth();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const subscriptionStatus = useMemo(() => {
		if (!user) {
			return {
				planTier: SUBSCRIPTION_PLANS.FREE,
				credits: 0,
				subscriptionEndDate: null,
				isPremium: false,
				unlockedChartHashes: [],
				unlockedCharts: [],
			};
		}

		return {
			planTier: user.planTier || SUBSCRIPTION_PLANS.FREE,
			credits: user.credits || 0,
			subscriptionEndDate: user.subscriptionEndDate || null,
			isPremium: user.planTier === SUBSCRIPTION_PLANS.PREMIUM,
			unlockedChartHashes: user.unlockedChartHashes || [],
			unlockedCharts: user.unlockedCharts || [],
		};
	}, [user]);

	const daysUntilExpiration = useMemo(() => {
		if (!subscriptionStatus.subscriptionEndDate) return null;

		const endDate = new Date(subscriptionStatus.subscriptionEndDate);
		const now = new Date();
		const diffTime = endDate - now;
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

		return Math.max(0, diffDays);
	}, [subscriptionStatus.subscriptionEndDate]);

	const isExpired = useMemo(() => {
		if (!subscriptionStatus.subscriptionEndDate) return false;
		return new Date(subscriptionStatus.subscriptionEndDate) < new Date();
	}, [subscriptionStatus.subscriptionEndDate]);

	const isExpiringSoon = useMemo(() => {
		return daysUntilExpiration !== null && daysUntilExpiration <= 7 && daysUntilExpiration > 0;
	}, [daysUntilExpiration]);

	const hasActiveSubscription = useMemo(() => {
		return (
			subscriptionStatus.isPremium &&
			!isExpired &&
			subscriptionStatus.subscriptionEndDate != null
		);
	}, [subscriptionStatus.isPremium, subscriptionStatus.subscriptionEndDate, isExpired]);

	const hasLowCredits = useMemo(() => {
		return hasActiveSubscription && subscriptionStatus.credits < 5;
	}, [hasActiveSubscription, subscriptionStatus.credits]);

	const refreshSubscription = useCallback(async () => {
		setLoading(true);
		setError(null);

		try {
			await refreshUser();
		} catch (err) {
			const errorMessage =
				err?.response?.data?.message ||
				err?.message ||
				"Failed to refresh subscription status";
			setError(errorMessage);
			console.error("Failed to refresh subscription:", err);
		} finally {
			setLoading(false);
		}
	}, [refreshUser]);

	const cancelSubscription = useCallback(async () => {
		setLoading(true);
		setError(null);

		try {
			await refreshUser();
			return { success: true };
		} catch (err) {
			const errorMessage =
				err?.response?.data?.message ||
				err?.message ||
				"Failed to cancel subscription";
			setError(errorMessage);
			throw err;
		} finally {
			setLoading(false);
		}
	}, [refreshUser]);

	const renewSubscription = useCallback(async () => {
		setLoading(true);
		setError(null);

		try {
			await refreshUser();
			return { success: true };
		} catch (err) {
			const errorMessage =
				err?.response?.data?.message ||
				err?.message ||
				"Failed to renew subscription";
			setError(errorMessage);
			throw err;
		} finally {
			setLoading(false);
		}
	}, [refreshUser]);

	const isChartUnlocked = useCallback(
		(chartHash) => {
			if (!chartHash) return false;
			return (user?.unlockedChartHashes || []).includes(chartHash);
		},
		[user?.unlockedChartHashes],
	);

	return {
		subscriptionStatus,
		credits: subscriptionStatus.credits,
		unlockedChartHashes: subscriptionStatus.unlockedChartHashes,
		unlockedCharts: subscriptionStatus.unlockedCharts,
		isChartUnlocked,
		daysUntilExpiration,
		isExpired,
		isExpiringSoon,
		hasLowCredits,
		hasActiveSubscription,
		isPremium: subscriptionStatus.isPremium,
		loading,
		error,
		refreshSubscription,
		cancelSubscription,
		renewSubscription,
	};
};
